/**
 * The filter AST, in THREE layers (#1803, design §5.9).
 *
 * One `Domain` type would be the obvious shape and it is the wrong one: the UI
 * needs a stable `id` to key a row being edited, and the wire must not carry
 * one — merging them sends UI state to the backend. So:
 *
 * | layer   | type              | job                                      |
 * |---------|-------------------|------------------------------------------|
 * | UI      | `ConditionRow`    | one editable QueryBuilder row (keeps `id`)|
 * | wire    | `Domain<M>`       | URL / request / preset (no `id`, `field` generic over the model) |
 * | backend | `FilterCondition` | what the list kernel actually parses      |
 *
 * This module sits beside `serialize.ts` rather than in `@resource-kit/core`
 * because `Domain` is generic over `FilterableKeys`, an OPEN registry only the
 * host can complete — fastcade does it once in `services/filterable-keys.ts`,
 * as a projection of `filters.generated.ts`. Core cannot carry an augmentation
 * target it never sees completed, and re-spelling the operator set inside core
 * is exactly the magic-string drift design principle 6 forbids. `OperatorId`
 * instead arrives from `@resource-kit/model`, where the closed set is
 * cross-checked against the backend's tuple by a parity test.
 */
import type { Condition, OperatorId } from '@resource-kit/model';

// ── Layer 1 · UI ─────────────────────────────────────────────────────────────

/**
 * One row of the filter builder. `id` is React-key state — stable across
 * re-renders so an input does not lose focus — and is NEVER serialized.
 */
export interface ConditionRow {
  id: string;
  field: string;
  op: OperatorId;
  value?: unknown;
}

/**
 * Compile-time seam check: the UI layer's row type must stay exactly the
 * QueryBuilder's `Condition`. A plain `export type ConditionRow = Condition`
 * would hide the seam; two silently diverging shapes would be worse. Consumed
 * by `domain.test.ts`, which fails to COMPILE if the two ever drift.
 */
export type ConditionRowMatchesCondition = ConditionRow extends Condition
  ? Condition extends ConditionRow
    ? true
    : false
  : false;

// ── Layer 2 · wire ───────────────────────────────────────────────────────────

/** The minimum a model must expose for its filterable keys to be looked up. */
export interface FilterableModel {
  name: string;
}

/**
 * Per-model filterable field names — the AUGMENTATION TARGET.
 *
 * Empty by construction: this module never learns a project's fields. The
 * project registers its models by declaration merging against its own codegen
 * whitelist (see `services/filterable-keys.ts`):
 *
 * ```ts
 * declare module '@resource-kit/controllers' {
 *   interface FilterableKeys { order: keyof typeof ORDER_FILTERS }
 * }
 * ```
 *
 * The augmentation names the PACKAGE ENTRY, not this file: a host cannot
 * reach a package's internal module path, and the host-side re-export shim is
 * not an augmentation target either — a declaration merged into the shim would
 * never reach the interface this file declares. That is why `index.ts` carries
 * this module as `export *` rather than a named list.
 */
export interface FilterableKeys {}

/**
 * The filterable subset of a model's field names.
 *
 * NOT `keyof T`. The authority is the backend whitelist, projected onto the
 * frontend by codegen (`services/filters.generated.ts`), because the kernel
 * answers 400 VALIDATION_FAILED for anything outside it. `keyof T` would let
 * the frontend build conditions the backend must reject — design principle 4
 * says the frontend may only ever be STRICTER than the backend, never looser.
 *
 * An unregistered model resolves to `never`: "this model has not been wired to
 * a whitelist yet" is then a compile error, not a runtime warning bar.
 */
export type FilterableKey<M extends FilterableModel> = M['name'] extends keyof FilterableKeys
  ? Extract<FilterableKeys[M['name']], string>
  : never;

/** A single `<field> <op> <value>` predicate. No `id` — this crosses the wire. */
export interface DomainLeaf<M extends FilterableModel> {
  field: FilterableKey<M>;
  op: OperatorId;
  value?: unknown;
}

/**
 * A node of the domain AST. A UNION with one member today — the shape, not the
 * content, is the point: adding `| DomainGroup<M>` (`{ op: 'or'; children:
 * readonly DomainNode<M>[] }`) later must not change the leaf. Implementing
 * `or` is an explicit non-goal of #1803; the backend kernel is a flat AND.
 */
export type DomainNode<M extends FilterableModel> = DomainLeaf<M>;

/** A whole filter: a flat AND of nodes, matching the backend kernel. */
export type Domain<M extends FilterableModel> = readonly DomainNode<M>[];

// ── Layer 3 · backend ────────────────────────────────────────────────────────

/**
 * What the list kernel parses
 * (`backend/src/platform/http/list-query.ts::parseListQuery` →
 * `backend/src/platform/db/list-filter.ts::buildListWhere`): a flat AND of
 * already-serialized
 * `<field>=<op>:<value>` pairs, each whitelisted per field. No `id`, no nesting.
 */
export interface FilterCondition {
  field: string;
  op: OperatorId;
  value: string;
}

// ── Same-field merge semantics ───────────────────────────────────────────────

/**
 * What happened when two or more UI rows targeted ONE field.
 *
 * Before #1803 the answer was "the last one silently overwrote the others"
 * (`serializeConditionsToParams` writes `params[field] = …` in a loop, and the
 * URL cannot hold two values for one key anyway). Silent is the defect: the
 * operator sees two chips and a list filtered by one of them. Every collapse
 * is now either a real merge or an announced override.
 */
export const CONDITION_MERGE = {
  /** N `eq` (and/or `in`) rows on one field become one `in`. */
  eqToIn: 'eq-to-in',
  /** A `from` row and a `to` row become one `between`. */
  rangeToBetween: 'range-to-between',
  /** No legal combination — the LAST row wins, and the UI says so. */
  lastWriteWins: 'last-write-wins',
} as const;

export type ConditionMergeKind = (typeof CONDITION_MERGE)[keyof typeof CONDITION_MERGE];

export interface ConditionMergeNotice {
  field: string;
  kind: ConditionMergeKind;
  /** How many UI rows collapsed into the single wire condition. */
  count: number;
}

export interface MergedConditions {
  /** At most ONE row per field — what the wire layer is built from. */
  conditions: ConditionRow[];
  /** One entry per field that collapsed. Empty means nothing was combined. */
  notices: ConditionMergeNotice[];
}

/** Flatten a row's value into the list of scalars an `in` set is built from. */
function valuesOf(row: ConditionRow): string[] {
  const value = row.value;
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value === undefined || value === null) return [];
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/**
 * Collapse multiple rows on one field into a single condition, per the table in
 * `CONDITION_MERGE`. Order-preserving and idempotent: re-running it over its own
 * output (which is what a URL round-trip does) changes nothing.
 *
 * A single row per field — the overwhelmingly common case, and the only case the
 * 20 shipped resources produce — passes through byte-identical, so the list URL
 * format is unchanged.
 */
export function mergeConditionRows(rows: readonly ConditionRow[]): MergedConditions {
  const byField = new Map<string, ConditionRow[]>();
  for (const row of rows) {
    const group = byField.get(row.field);
    if (group) group.push(row);
    else byField.set(row.field, [row]);
  }

  const conditions: ConditionRow[] = [];
  const notices: ConditionMergeNotice[] = [];

  for (const [field, group] of byField) {
    if (group.length === 1) {
      conditions.push(group[0]);
      continue;
    }

    const ops = new Set(group.map((r) => r.op));

    // N × (eq | in) ⇒ one `in` over the union of their values.
    if ([...ops].every((op) => op === 'eq' || op === 'in')) {
      const seen = new Set<string>();
      for (const row of group) {
        for (const value of valuesOf(row)) seen.add(value);
      }
      conditions.push({ id: `${field}::in`, field, op: 'in', value: [...seen] });
      notices.push({ field, kind: CONDITION_MERGE.eqToIn, count: group.length });
      continue;
    }

    // from + to ⇒ one `between`. Both bounds must be present and non-empty;
    // a half-open range has no `between` form, so it falls through to the
    // announced override below rather than being invented.
    if (group.length === 2 && ops.has('from') && ops.has('to')) {
      const lower = group.find((r) => r.op === 'from');
      const upper = group.find((r) => r.op === 'to');
      const lo = lower ? String(lower.value ?? '') : '';
      const hi = upper ? String(upper.value ?? '') : '';
      if (lo !== '' && hi !== '') {
        conditions.push({
          id: `${field}::between`,
          field,
          op: 'between',
          value: [lo, hi] as [string, string],
        });
        notices.push({ field, kind: CONDITION_MERGE.rangeToBetween, count: 2 });
        continue;
      }
    }

    // Nothing legal to merge: last write wins — ANNOUNCED, never silent.
    conditions.push(group[group.length - 1]);
    notices.push({ field, kind: CONDITION_MERGE.lastWriteWins, count: group.length });
  }

  return { conditions, notices };
}

/**
 * UI rows ⇒ the wire layer: merge same-field rows, then drop `id`. The cast on
 * `field` is the one place the two vocabularies meet — the caller asserts the
 * rows were built against `M`'s whitelist, which is what `FilterableKey<M>`
 * makes checkable at every construction site upstream.
 */
export function toDomain<M extends FilterableModel>(
  rows: readonly ConditionRow[],
): { domain: Domain<M>; notices: ConditionMergeNotice[] } {
  const { conditions, notices } = mergeConditionRows(rows);
  const domain = conditions.map((c): DomainNode<M> => ({
    field: c.field as FilterableKey<M>,
    op: c.op,
    value: c.value,
  }));
  return { domain, notices };
}

/** Wire ⇒ UI: give every node the stable row id the builder edits by. */
export function toConditionRows<M extends FilterableModel>(domain: Domain<M>): ConditionRow[] {
  return domain.map((node) => ({
    id: `${node.field}::${node.op}`,
    field: node.field,
    op: node.op,
    value: node.value,
  }));
}
